import * as Schema from "effect/Schema";

/**
 * Workspace readiness phase enum — the typed lifecycle the master streams
 * to the SPA so the loading UX can reflect real provisioning state instead
 * of time-derived heuristics.
 *
 * Tracking: FINDING-CB1 (project_forge_user_vm_429_amplifier derivative).
 * See docs/superpowers/specs/2026-05-19-forge-cold-boot-progress-channel-design.md.
 *
 * Phases are runtime-neutral. Forge Cloud, Desktop, and dev-local emit the
 * same names; only transitions differ. Desktop walks `idle → starting →
 * app-ready` in <2s, Cloud may walk the full enum.
 */
export const WorkspacePhase = Schema.Union([
  Schema.Literal("idle"),
  Schema.Literal("queued"),
  Schema.Literal("listing"),
  Schema.Literal("rate-limited"),
  Schema.Literal("creating"),
  Schema.Literal("starting"),
  Schema.Literal("waiting-state"),
  Schema.Literal("image-pulling"),
  Schema.Literal("supervisor-up"),
  Schema.Literal("app-ready"),
  Schema.Literal("reconnecting"),
  Schema.Literal("error"),
]);
export type WorkspacePhase = typeof WorkspacePhase.Type;

/**
 * Phase event payload. All fields are user-display-safe — no internal Fly
 * DNS, no machineId, no userId, no tokens (Codex Eng review finding).
 *
 * Fields are union-discriminated by `phase` so consumers can switch on it
 * to access phase-specific payload data.
 */
export const PhaseEvent = Schema.Union([
  Schema.Struct({
    phase: Schema.Literal("idle"),
    ts: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("queued"),
    ts: Schema.Number,
    estimatedRemainingMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("listing"),
    ts: Schema.Number,
    estimatedRemainingMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("rate-limited"),
    ts: Schema.Number,
    retryAfterMs: Schema.Number,
    estimatedRemainingMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("creating"),
    ts: Schema.Number,
    estimatedRemainingMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("starting"),
    ts: Schema.Number,
    estimatedRemainingMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("waiting-state"),
    ts: Schema.Number,
    elapsedMs: Schema.Number,
    timeoutMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("image-pulling"),
    ts: Schema.Number,
    estimatedRemainingMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("supervisor-up"),
    ts: Schema.Number,
    estimatedRemainingMs: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("app-ready"),
    ts: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("reconnecting"),
    ts: Schema.Number,
  }),
  Schema.Struct({
    phase: Schema.Literal("error"),
    ts: Schema.Number,
    code: Schema.String,
    message: Schema.String,
    retryable: Schema.Boolean,
  }),
]);
export type PhaseEvent = typeof PhaseEvent.Type;

/**
 * Subscribe input — empty for now; the user is identified by the auth
 * session, not the payload. Reserved for future per-thread or per-workspace
 * scoping.
 */
export const WorkspaceReadinessSubscribeInput = Schema.Struct({});
export type WorkspaceReadinessSubscribeInput = typeof WorkspaceReadinessSubscribeInput.Type;

export class WorkspaceReadinessError extends Schema.TaggedErrorClass<WorkspaceReadinessError>()(
  "WorkspaceReadinessError",
  {
    code: Schema.String,
    message: Schema.String,
  },
) {}
